/** Frontend mirror of backend display rollup (fallback for older saved datasets). */

const DAY = 86400;

function toUnixSeconds(ts) {
  const n = Number(ts || 0);
  if (!n) return 0;
  return n > 1e12 ? Math.floor(n / 1000) : Math.floor(n);
}

function utcDayStart(ts) {
  return Math.floor(toUnixSeconds(ts) / DAY) * DAY;
}

function utcDayEnd(ts) {
  return utcDayStart(ts) + DAY - 1;
}

function utcDayKey(ts) {
  return new Date(utcDayStart(ts) * 1000).toISOString().slice(0, 10);
}

export function isGeneratedVthoInterval(row = {}) {
  if (String(row?.token || "").toUpperCase() !== "VTHO") return false;
  if (String(row?.classification || row?.type || "").toLowerCase() !== "generated") {
    return false;
  }
  if (row?.syntheticVthoDailyRollup === true) return false;
  if (String(row?.hash || "").startsWith("vtho-generated-day:")) return false;
  return (
    row?.syntheticVthoAccrual === true ||
    String(row?.hash || "").startsWith("vtho-generated:")
  );
}

function pickFiat(row = {}) {
  const taxable = row.taxableProfit ?? row.taxableProfitEUR ?? row.taxableProfitFiat;
  if (taxable != null && Number.isFinite(Number(taxable))) return Number(taxable);
  if (row.euroAmount != null && Number.isFinite(Number(row.euroAmount))) {
    return Number(row.euroAmount);
  }
  if (row.amountFiat != null && Number.isFinite(Number(row.amountFiat))) {
    return Number(row.amountFiat);
  }
  return 0;
}

function dayKeyToEndTs(dayKey) {
  return utcDayEnd(Math.floor(new Date(`${dayKey}T00:00:00Z`).getTime() / 1000));
}

function finalizeBucket(b) {
  const amount = Number(b.amount || 0);
  const euroAmount = Number(b.euroAmount || 0);
  const vetAmount =
    b.coveredSec > 0 ? b.weightedVetSeconds / b.coveredSec : Number(b.vetAmount || 0);

  return {
    classification: "generated",
    type: "generated",
    token: "VTHO",
    timestamp: b.timestamp,
    ts: b.timestamp,
    hash: `vtho-generated-day:${b.day}`,
    amount,
    euroPrice: amount > 0 ? euroAmount / amount : 0,
    euroAmount,
    taxableProfit: euroAmount,
    taxableProfitEUR: euroAmount,
    taxableProfitFiat: euroAmount,
    amountFiat: euroAmount,
    totalFiat: euroAmount,
    vetAmount,
    vetBalanceDuringInterval: vetAmount,
    generatedFromTs: b.generatedFromTs,
    generatedToTs: b.generatedToTs,
    intervalDurationSec: b.coveredSec,
    intervalEndReason: "daily_rollup",
    generationSource: "legacy_vet_continuous_daily_rollup",
    syntheticVthoAccrual: true,
    syntheticVthoDailyRollup: true,
    sourceIntervalCount: b.sourceIntervalCount,
    fifoLegs: [],
  };
}

function addToBucket(buckets, dayKey, patch) {
  const b =
    buckets.get(dayKey) ||
    {
      day: dayKey,
      timestamp: dayKeyToEndTs(dayKey),
      amount: 0,
      euroAmount: 0,
      coveredSec: 0,
      weightedVetSeconds: 0,
      sourceIntervalCount: 0,
      generatedFromTs: patch.generatedFromTs,
      generatedToTs: patch.generatedToTs,
      vetAmount: patch.vetAmount || 0,
    };

  b.amount += patch.amount;
  b.euroAmount += patch.euroAmount;
  b.coveredSec += patch.sec;
  b.weightedVetSeconds += patch.weightedVetSeconds;
  b.sourceIntervalCount += 1;
  b.generatedFromTs = Math.min(b.generatedFromTs, patch.generatedFromTs);
  b.generatedToTs = Math.max(b.generatedToTs, patch.generatedToTs);
  b.vetAmount = patch.vetAmount || b.vetAmount;
  buckets.set(dayKey, b);
}

function splitIntervalAcrossUtcDays(from, to, amount, fiat) {
  const totalSec = to - from;
  if (!(totalSec > 0)) return [];

  const parts = [];
  let dayStart = utcDayStart(from);
  while (dayStart < to) {
    const partStart = Math.max(from, dayStart);
    const partEnd = Math.min(to, dayStart + DAY);
    const sec = Math.max(0, partEnd - partStart);
    if (sec > 0) {
      parts.push({
        partStart,
        partEnd,
        sec,
        dayKey: utcDayKey(partStart),
        ratio: sec / totalSec,
      });
    }
    dayStart += DAY;
  }

  let allocatedAmount = 0;
  let allocatedFiat = 0;
  return parts.map((part, i) => {
    const isLast = i === parts.length - 1;
    const partAmount = isLast
      ? amount - allocatedAmount
      : amount * part.ratio;
    const partFiat = isLast
      ? fiat - allocatedFiat
      : fiat * part.ratio;
    if (!isLast) {
      allocatedAmount += partAmount;
      allocatedFiat += partFiat;
    }
    return { ...part, amount: partAmount, euroAmount: partFiat };
  });
}

export function rollupGeneratedVthoByDay(rows = []) {
  const buckets = new Map();
  let sourceAmount = 0;
  let sourceFiat = 0;

  for (const row of rows || []) {
    if (!isGeneratedVthoInterval(row)) continue;

    const amount = Number(row.amount || 0);
    const fiat = pickFiat(row);
    if (!(amount > 0) && !(fiat > 0)) continue;

    sourceAmount += amount;
    sourceFiat += fiat;

    const from = Number(row.generatedFromTs || 0);
    const to = Number(row.generatedToTs || row.timestamp || row.ts || 0);
    const vetBalance = Number(row.vetBalanceDuringInterval ?? row.vetAmount ?? 0);

    if (from > 0 && to > from) {
      const dayParts = splitIntervalAcrossUtcDays(from, to, amount, fiat);
      for (const part of dayParts) {
        addToBucket(buckets, part.dayKey, {
          amount: part.amount,
          euroAmount: part.euroAmount,
          sec: part.sec,
          weightedVetSeconds: vetBalance * part.sec,
          vetAmount: vetBalance,
          generatedFromTs: part.partStart,
          generatedToTs: part.partEnd,
        });
      }
      continue;
    }

    const ts = toUnixSeconds(row.timestamp || row.ts || 0);
    if (!(ts > 0)) continue;
    const dayKey = utcDayKey(ts);
    addToBucket(buckets, dayKey, {
      amount,
      euroAmount: fiat,
      sec: Number(row.intervalDurationSec || DAY),
      weightedVetSeconds: vetBalance * Number(row.intervalDurationSec || DAY),
      vetAmount: vetBalance,
      generatedFromTs: from || utcDayStart(ts),
      generatedToTs: to || utcDayEnd(ts),
    });
  }

  const finalized = [...buckets.values()]
    .map((b) => {
      b.timestamp = dayKeyToEndTs(b.day);
      return finalizeBucket(b);
    })
    .sort((a, b) => Number(a.timestamp || 0) - Number(b.timestamp || 0));

  if (finalized.length > 0) {
    const rolledAmount = finalized.reduce((s, r) => s + Number(r.amount || 0), 0);
    const rolledFiat = finalized.reduce((s, r) => s + Number(r.euroAmount || 0), 0);
    const amountDiff = sourceAmount - rolledAmount;
    const fiatDiff = sourceFiat - rolledFiat;
    if (Math.abs(amountDiff) > 0 || Math.abs(fiatDiff) > 0) {
      const last = finalized[finalized.length - 1];
      last.amount = Number(last.amount || 0) + amountDiff;
      last.euroAmount = Number(last.euroAmount || 0) + fiatDiff;
      last.taxableProfit = last.euroAmount;
      last.taxableProfitEUR = last.euroAmount;
      last.taxableProfitFiat = last.euroAmount;
      last.amountFiat = last.euroAmount;
      last.totalFiat = last.euroAmount;
      last.euroPrice = last.amount > 0 ? last.euroAmount / last.amount : 0;
    }
  }

  return finalized;
}

/** Replace interval generated rows with daily display rows when needed. */
export function applyVthoGeneratedDisplayRollup(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  const hasDailyRollup = list.some(
    (row) =>
      row?.syntheticVthoDailyRollup === true ||
      String(row?.hash || "").startsWith("vtho-generated-day:")
  );

  if (hasDailyRollup) {
    return list
      .filter((row) => !isGeneratedVthoInterval(row))
      .sort(
        (a, b) => Number(a.timestamp || a.ts || 0) - Number(b.timestamp || b.ts || 0)
      );
  }

  const intervalRows = list.filter(isGeneratedVthoInterval);
  if (intervalRows.length <= 1) return list;

  const otherRows = list.filter((row) => !isGeneratedVthoInterval(row));
  const rolled = rollupGeneratedVthoByDay(intervalRows);
  return [...otherRows, ...rolled].sort(
    (a, b) => Number(a.timestamp || a.ts || 0) - Number(b.timestamp || b.ts || 0)
  );
}
